import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  CookieCategory,
  CookieConsentState,
  NO_CONSENT,
  clearCategoryCookies,
  customConsent,
  getAnonymousSessionId,
  grantAll,
  persistConsent,
  readStoredConsent,
  rejectNonEssential,
  storeConsent,
} from "lib/consent/cookieConsent";
import { applyConsentToScripts, needsReloadToRevoke } from "lib/consent/scriptGating";

type CookieConsentContextValue = {
  /** Null until a decision has been made. Null means show the banner. */
  consent: CookieConsentState | null;
  /** Effective state — NO_CONSENT while undecided, so callers never see null. */
  effective: CookieConsentState;
  hasDecided: boolean;
  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustom: (choices: Record<CookieCategory, boolean>) => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export const CookieConsentProvider = ({ children }: { children: React.ReactNode }) => {
  // Read synchronously on first render. Nothing non-essential can have fired
  // yet, because nothing injects a script until applyConsentToScripts runs below.
  const [consent, setConsent] = useState<CookieConsentState | null>(() => readStoredConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Apply the stored decision on mount. A visitor who accepted analytics last
  // week gets the tag; a visitor with no decision gets nothing.
  useEffect(() => {
    if (consent) {
      applyConsentToScripts(consent);
    }
    // Intentionally mount-only: later changes go through `apply` below, which
    // needs the previous value to decide whether a reload is required.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = useCallback(
    (next: CookieConsentState) => {
      const previous = consent;

      // 1. Store and apply locally FIRST, so the decision takes effect with no
      //    dependency on the network. The routed-participant flow must never
      //    wait on a consent round-trip.
      storeConsent(next);
      setConsent(next);
      setSettingsOpen(false);

      // 2. Remove cookies for anything now switched off. Turning a category off
      //    has to actually clear what it set, not merely stop adding more.
      (["analytics", "functional", "marketing"] as CookieCategory[]).forEach((category) => {
        if (previous?.[category] && !next[category]) {
          clearCategoryCookies(category);
        }
      });

      // 3. Persist for the audit trail. Fire-and-forget by design.
      persistConsent(next, getAnonymousSessionId());

      // 4. A script already evaluated cannot be unloaded from a live document,
      //    so a revocation reloads. Only when something really was loaded and is
      //    now withdrawn -- a first-time decision never reloads.
      if (needsReloadToRevoke(previous, next)) {
        window.location.reload();
        return;
      }

      applyConsentToScripts(next);
    },
    [consent]
  );

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      effective: consent ?? NO_CONSENT,
      hasDecided: consent !== null,
      settingsOpen,
      openSettings: () => setSettingsOpen(true),
      closeSettings: () => setSettingsOpen(false),
      acceptAll: () => apply(grantAll()),
      rejectAll: () => apply(rejectNonEssential()),
      saveCustom: (choices) => apply(customConsent(choices)),
    }),
    [consent, settingsOpen, apply]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = (): CookieConsentContextValue => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};
